import { Archive, ChevronRight, Threads } from '@/components/ui/icons';
import { useNavigate } from 'react-router';
import type { ThreadDTO } from '@clanfire/shared';
import { formatRelative } from '@/lib/time';
import { cn } from '@/lib/utils';
import { threadPath, useThread, useThreadUnread } from '@/stores/threads';

/** The card under a message that has a thread: its name, size and last activity. Opens the thread. */
export function ThreadChip({ thread: initial, className }: { thread: ThreadDTO; className?: string }) {
  // The store has the latest numbers once anyone has written in it.
  const thread = useThread(initial.id) ?? initial;
  const unread = useThreadUnread(thread);
  const navigate = useNavigate();
  const count = thread.messageCount;
  return (
    <button
      type="button"
      onClick={() => navigate(threadPath(thread))}
      aria-label={`Open thread ${thread.name}${unread ? ', unread messages' : ''}`}
      className={cn(
        'mt-1.5 flex w-full max-w-md items-center gap-2.5 rounded-xl border border-line bg-sunken px-3 py-2 text-left transition-colors hover:border-line-strong hover:bg-hover',
        className,
      )}
    >
      <Threads size={18} className="shrink-0 text-accent" />
      <span className="min-w-0 flex-1">
        <span className={cn('flex items-center gap-1.5 truncate text-sm', unread ? 'font-bold text-fg' : 'font-semibold text-fg-soft')}>
          <span className="truncate">{thread.name}</span>
          {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden />}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-fg-muted">
          {count === 0 ? 'No messages yet' : `${count} message${count === 1 ? '' : 's'}`}
          {thread.lastMessageAt && count > 0 && <> · {formatRelative(thread.lastMessageAt)}</>}
          {thread.archivedAt && (
            <>
              {' '}
              · <Archive size={12} aria-hidden /> Archived
            </>
          )}
        </span>
      </span>
      <ChevronRight size={16} className="shrink-0 text-fg-muted" />
    </button>
  );
}
